"use client";

import { Link } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  /** Days remaining on the free trial. null when the end date is unknown. */
  trialDaysRemaining: number | null;
  /** When the trial ends (ms epoch). null when unknown. */
  trialEndsAt: number | null;
  /** Current path — used to build the /pricing `next` return URL. */
  nextPath?: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(msEpoch: number): string {
  return new Date(msEpoch).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function buildUpgradeHref(nextPath?: string): string {
  const q = new URLSearchParams({
    upgrade: "1",
    reason: "trial_view_only",
    source: "trial_view_only_banner",
    ...(nextPath ? { next: nextPath } : {}),
  });
  return `/pricing?${q.toString()}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * TrialViewOnlyBanner
 *
 * Displayed by `ServerProvisioningGate` at the top of the servers dashboard
 * when a user is on a self-service free trial in view-only mode.
 *
 * Communicates:
 * 1. That the user is in view-only mode (server actions are disabled)
 * 2. How many days are left on the trial (or the expiry date)
 * 3. A direct CTA to upgrade and unlock full access
 *
 * Design: amber warning strip, consistent with `SubscriptionBanner` styling.
 */
export function TrialViewOnlyBanner({
  trialDaysRemaining,
  trialEndsAt,
  nextPath,
}: Props) {
  const upgradeHref = buildUpgradeHref(nextPath);

  const timeText = (() => {
    if (trialDaysRemaining !== null) {
      if (trialDaysRemaining === 0) return "expires today";
      if (trialDaysRemaining === 1) return "1 day left";
      return `${trialDaysRemaining} days left`;
    }
    if (trialEndsAt !== null) return `expires ${formatDate(trialEndsAt)}`;
    return "trial active";
  })();

  return (
    <div
      role="alert"
      className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
    >
      {/* Left: icon + message */}
      <div className="flex items-center gap-3">
        {/* Lock icon */}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
        >
          <path
            fillRule="evenodd"
            d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
            clipRule="evenodd"
          />
        </svg>

        <span>
          <strong className="font-semibold">Free Trial — View Only</strong>
          {" · "}
          Server actions are disabled during your trial ({timeText}).{" "}
          <Link
            to={upgradeHref}
            className="font-medium underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-100"
          >
            Upgrade to unlock full access.
          </Link>
        </span>
      </div>

      {/* Right: upgrade button */}
      <Link
        to={upgradeHref}
        className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 dark:bg-amber-500 dark:hover:bg-amber-400"
      >
        Upgrade Now
      </Link>
    </div>
  );
}
